// packages/widget/src/components/steps/availability-reason-copy.ts
//
// #521 follow-up — ONE source of truth for "why are there no times?".
//
// The reason arrives from the availability endpoint (`reason` on the response and
// on each unbookable slot). Two places tell the guest about it: the rich recovery
// block (`step-datetime-recovery.ts`, gated behind `recoveryEnabled`) and the bare
// empty state in `step-datetime.ts` that every host WITHOUT that flag renders —
// which is the hosted `public/book.html` path, i.e. most real guests.
//
// Before this module the honest sentence existed only on the gated path, so the
// ungated one still said "No times available for this date." and blamed the DATE
// for what was really a PARTY-SIZE constraint. Same shared-implementation
// discipline #521 itself used (canSeatParty delegating to recommendTables): the
// flag may decide how much recovery UI to show, never whether the guest is told
// the truth.
//
// Returns `null` for an absent or unrecognised reason so each caller keeps its own
// established default — an unknown future reason must never render as "null" or
// silently replace working copy.

/**
 * The single fallback for "there is nothing bookable and the server did not say
 * why" — previously duplicated as two different sentences in the two callers.
 *
 * This wording wins over the recovery block's old "No tables open for this date."
 * because it is the long-standing guest-facing string AND the expectation written
 * into the UAT script (`src/pages/UATPage.jsx` case 21.8); changing it would
 * silently invalidate a documented test. The recovery default was only ever
 * reachable behind `recoveryEnabled`, which no host currently sets.
 */
export const NO_AVAILABILITY_DEFAULT = 'No times available for this date.';

/**
 * True when trying a different DATE cannot possibly help.
 *
 * Only `no_table_for_party` qualifies: no date changes how big the tables are, so
 * sending this guest back to the calendar wastes their time. Every other reason the
 * server emits (`blocked`, `meal_cap_full`, `online_section_full`) is date-specific
 * and another date genuinely might work.
 */
export function availabilityReasonIsDeadEnd(reason: string | null | undefined): boolean {
  // #591: over the venue's online cap is equally date-independent.
  return reason === 'no_table_for_party' || reason === 'party_too_large';
}

export function availabilityReasonHeadline(
  reason: string | null | undefined,
  partySize?: number,
): string | null {
  switch (reason) {
    case 'closed':
      return "We're closed on this date.";
    case 'past':
      return 'That date has already passed.';
    case 'fully_booked':
      return 'Fully booked for this date.';
    case 'party_too_large':
      // #591 — the venue's own online ceiling: the answer is the phone, not
      // another date or time.
      return 'For a party this size, please contact the restaurant directly.';
    case 'no_table_for_party':
      // Name the real constraint. Sending this guest to try another date wastes
      // their time — no date will help, because no table is big enough.
      return Number.isFinite(partySize) && (partySize as number) > 0
        ? `We can't seat a party of ${partySize} online.`
        : "We can't seat a party this size online.";
    default:
      return null;
  }
}
